import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import UserSidebar from "@/components/UserSidebar";
import { Package, CheckCircle, Truck, Clock, Store, ShipIcon, Coins, CalendarClock, Square, CheckSquare, ChevronDown, Minus, X } from "lucide-react";
import { fetchProductPage, confirmReceipt, convertToPoints, type Product, type ProductStatusFilter } from "@/services/customerService";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";
import ResponsiveImage from "@/components/ResponsiveImage";

type Tab = 'pending' | 'shipping' | 'shipped' | 'converted' | 'other';

const statusLabels: Record<string, string> = {
  preparing: '準備中',
  shipped: '発送済み',
  in_transit: '配送中',
  delivered: '配達完了',
  received: '受取確認済み',
  cancelled: 'キャンセル',
  returned: '返送'
};

// PT交換可能かどうかを判定（発送予定日を過ぎていたら不可）
const canConvertToPoints = (product: Product): boolean => {
  if (!product.allow_point_conversion || product.point_value <= 0) return false;
  if (product.auto_ship_deadline && new Date(product.auto_ship_deadline) < new Date()) return false;
  return true;
};

const canRequestShipping = (product: Product): boolean => {
  return product.status === 'pending' && product.can_request_shipping !== false && !product.auto_ship_deadline;
};

const isPast = (value?: string | null): boolean => {
  return Boolean(value && new Date(value) < new Date());
};

const OripaProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmingReceipt, setConfirmingReceipt] = useState<number | null>(null);
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [displayCount, setDisplayCount] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedShippingIds, setSelectedShippingIds] = useState<Set<number>>(new Set());
  const latestRequestId = useRef(0);

  const activeTab = (searchParams.get('tab') as Tab) || 'pending';

  useEffect(() => {
    setDisplayCount(20);
    setSelectedShippingIds(new Set());
    loadProducts(false, 1, false);
  }, [activeTab]);

  const loadProducts = async (silent = false, page = 1, append = false): Promise<boolean> => {
    const requestId = ++latestRequestId.current;
    try {
      if (!silent) setLoading(true);
      if (append) setLoadingMore(true);
      const status = (activeTab === 'other' ? 'invalidated' : activeTab) as ProductStatusFilter;
      const data = await fetchProductPage({ eventType: 'oripa', status, page, perPage: 100 });
      if (requestId !== latestRequestId.current) return false;
      setProducts(previous => append ? [...previous, ...data.products] : data.products);
      setCurrentPage(data.pagination.page);
      setHasMore(data.pagination.has_more);
      return true;
    } catch (error) {
      if (requestId === latestRequestId.current) {
        toast.error('商品の取得に失敗しました');
        console.error(error);
      }
      return false;
    } finally {
      if (requestId === latestRequestId.current) {
        if (!silent) setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const filteredProducts = products.filter(p => {
    if (activeTab === 'pending') {
      return p.status === 'pending';
    }
    if (activeTab === 'other') {
      return p.status === 'invalidated';
    }
    return p.status === activeTab;
  });

  const productsByOrder = useMemo(() => {
    if (activeTab !== 'shipping' && activeTab !== 'shipped') return [];

    const relevantProducts = activeTab === 'shipped'
      ? products.filter(p => p.status === 'shipped' || (p.order_status === 'received'))
      : filteredProducts;

    const grouped = new Map<number, { order_id: number; site: Product['site']; order_status: string; products: Product[] }>();

    relevantProducts.forEach(product => {
      const orderId = product.order_id!;
      if (!grouped.has(orderId)) {
        grouped.set(orderId, {
          order_id: orderId,
          site: product.site,
          order_status: product.order_status || 'preparing',
          products: []
        });
      }
      grouped.get(orderId)!.products.push(product);
    });

    return Array.from(grouped.values());
  }, [activeTab, filteredProducts, products]);

  const handleTabChange = (tab: Tab) => {
    setSearchParams({ tab });
  };

  const handleLoadMore = async () => {
    const renderedCount = activeTab === 'shipping' || activeTab === 'shipped' ? productsByOrder.length : filteredProducts.length;
    if (displayCount < renderedCount) {
      setDisplayCount(previous => previous + 20);
    } else if (hasMore) {
      const loaded = await loadProducts(true, currentPage + 1, true);
      if (loaded) setDisplayCount(previous => previous + 20);
    }
  };

  const handleShipSingle = (lotteryResultId: number) => {
    navigate('/products/confirm', { state: { lotteryResultIds: [lotteryResultId] } });
  };

  const shippablePendingProducts = products.filter(canRequestShipping);

  const selectedShippingProducts = shippablePendingProducts.filter(product =>
    selectedShippingIds.has(product.lottery_result_id)
  );

  const toggleShippingSelection = (lotteryResultId: number) => {
    setSelectedShippingIds(prev => {
      const next = new Set(prev);
      if (next.has(lotteryResultId)) {
        next.delete(lotteryResultId);
      } else {
        next.add(lotteryResultId);
      }
      return next;
    });
  };

  const toggleAllShippingSelection = () => {
    setSelectedShippingIds(prev => {
      const allSelected = shippablePendingProducts.every(product => prev.has(product.lottery_result_id));
      if (allSelected) return new Set();
      return new Set(shippablePendingProducts.map(product => product.lottery_result_id));
    });
  };

  const handleBulkShipping = () => {
    if (selectedShippingProducts.length === 0) return;

    navigate('/products/confirm', {
      state: { lotteryResultIds: selectedShippingProducts.map(product => product.lottery_result_id) },
    });
  };

  const handleConfirmReceipt = async (orderId: number) => {
    try {
      setConfirmingReceipt(orderId);
      await confirmReceipt(orderId);
      toast.success('受取確認が完了しました');
      await loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || '受取確認に失敗しました');
      console.error(error);
    } finally {
      setConfirmingReceipt(null);
    }
  };

  const handleConvertSingle = async (product: Product) => {
    if (!product.allow_point_conversion || product.point_value <= 0) {
      toast.error('この商品はポイント交換できません');
      return;
    }

    const confirmMessage = `「${product.name}」を${product.point_value.toLocaleString()}PTに交換しますか？\n※この操作は取り消せません`;
    if (!confirm(confirmMessage)) return;

    try {
      setConvertingId(product.lottery_result_id);
      const result = await convertToPoints([product.lottery_result_id]);
      toast.success(result.message);
      // ポイント残高を更新
      refreshUser();
      await loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'ポイント交換に失敗しました');
      console.error(error);
    } finally {
      setConvertingId(null);
    }
  };

  // 一括PT交換
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkExcludedIds, setBulkExcludedIds] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [bulkConverting, setBulkConverting] = useState(false);

  const convertiblePendingProducts = products.filter(p => p.status === 'pending' && canConvertToPoints(p));

  const convertibleGroups = useMemo(() => {
    const groups = new Map<string, { name: string; image: string; variants: any; rarity: string; pointValue: number; items: Product[] }>();
    convertiblePendingProducts.forEach(p => {
      if (!groups.has(p.name)) {
        groups.set(p.name, { name: p.name, image: p.image_urls[0], variants: p.image_variant_urls, rarity: p.rarity, pointValue: p.point_value, items: [] });
      }
      groups.get(p.name)!.items.push(p);
    });
    return Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
  }, [convertiblePendingProducts]);

  const BULK_CONVERT_LIMIT = 100;
  const selectedForBulk = convertiblePendingProducts.filter(p => !bulkExcludedIds.has(p.lottery_result_id));
  const bulkTotalPoints = selectedForBulk.reduce((sum, p) => sum + p.point_value, 0);
  const isBulkOverLimit = selectedForBulk.length > BULK_CONVERT_LIMIT;

  const getGroupSelectionState = (group: { items: Product[] }): 'all' | 'partial' | 'none' => {
    const excludedCount = group.items.filter(p => bulkExcludedIds.has(p.lottery_result_id)).length;
    if (excludedCount === 0) return 'all';
    if (excludedCount === group.items.length) return 'none';
    return 'partial';
  };

  const toggleGroupSelection = (group: { items: Product[] }) => {
    setBulkExcludedIds(prev => {
      const next = new Set(prev);
      const state = getGroupSelectionState(group);
      if (state === 'all') {
        group.items.forEach(p => next.add(p.lottery_result_id));
      } else {
        group.items.forEach(p => next.delete(p.lottery_result_id));
      }
      return next;
    });
  };

  const toggleItemSelection = (id: number) => {
    setBulkExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroupExpand = (name: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const openBulkDialog = () => {
    setBulkExcludedIds(new Set());
    setExpandedGroups(new Set());
    setShowBulkDialog(true);
  };

  const handleBulkConvert = async () => {
    if (selectedForBulk.length === 0) return;
    try {
      setBulkConverting(true);
      const ids = selectedForBulk.map(p => p.lottery_result_id);
      const result = await convertToPoints(ids);
      toast.success(result.message);
      refreshUser();
      await loadProducts();
      setShowBulkDialog(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'ポイント交換に失敗しました');
    } finally {
      setBulkConverting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>獲得した賞品</title>
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div>
          <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
            <div className="flex gap-8">
              <div className="hidden lg:block">
                <UserSidebar />
              </div>

              <section className="flex-1 min-w-0">
                <nav className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600 mb-4">
                  <ol className="flex items-center gap-1">
                    <li>
                      <Link to="/" className="hover:text-theme-primary underline underline-offset-2">トップ</Link>
                    </li>
                    <li className="text-black">/</li>
                    <li className="text-black">マイコレクション</li>
                  </ol>
                </nav>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                    <Package className="h-5 w-5 text-theme-button" />
                  </div>
                  <div>
                    <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">獲得した賞品</h1>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">抽選で獲得した賞品を管理できます</p>
                  </div>
                </div>

          <div className="flex gap-2 mb-6 overflow-x-auto">
            <button
              onClick={() => handleTabChange('pending')}
              className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${
                activeTab === 'pending'
                  ? 'bg-theme-primary text-theme-button shadow-theme-sm'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Clock className="inline h-4 w-4 mr-1" />
              未選択
            </button>
            <button
              onClick={() => handleTabChange('shipping')}
              className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${
                activeTab === 'shipping'
                  ? 'bg-theme-primary text-theme-button shadow-theme-sm'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Package className="inline h-4 w-4 mr-1" />
              発送依頼中
            </button>
            <button
              onClick={() => handleTabChange('shipped')}
              className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${
                activeTab === 'shipped'
                  ? 'bg-theme-primary text-theme-button shadow-theme-sm'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <CheckCircle className="inline h-4 w-4 mr-1" />
              発送済
            </button>
            <button
              onClick={() => handleTabChange('converted')}
              className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${
                activeTab === 'converted'
                  ? 'bg-theme-primary text-theme-button shadow-theme-sm'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Coins className="inline h-4 w-4 mr-1" />
              PT交換済
            </button>
            <button
              onClick={() => handleTabChange('other')}
              className={`ml-auto px-2 py-2 [font-weight:var(--theme-font-weight-button)] text-xs transition-colors whitespace-nowrap ${
                activeTab === 'other'
                  ? 'text-theme-bg-text underline underline-offset-4'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              対象外
            </button>
          </div>

          {activeTab === 'pending' && !loading && convertiblePendingProducts.length > 0 && (
            <button
              onClick={openBulkDialog}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-4 bg-green-400 border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
            >
              <Coins className="h-4 w-4" />
              一括PT交換（{convertiblePendingProducts.length}件 / {convertiblePendingProducts.reduce((s, p) => s + p.point_value, 0).toLocaleString()} PT）
            </button>
          )}

          {activeTab === 'pending' && !loading && shippablePendingProducts.length > 1 && (
            <div className="bg-white border-theme rounded-theme-lg shadow-theme p-4 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-sm [font-weight:var(--theme-font-weight)] text-black">まとめて発送</h2>
                  <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600 mt-1">
                    発送したい商品を選択して、まとめて配送申請できます。
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
                    {selectedShippingProducts.length}件選択中
                  </span>
                  <button
                    onClick={toggleAllShippingSelection}
                    className="px-3 py-2 bg-white border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                  >
                    {selectedShippingProducts.length === shippablePendingProducts.length ? '選択解除' : 'すべて選択'}
                  </button>
                  <button
                    onClick={handleBulkShipping}
                    disabled={selectedShippingProducts.length === 0}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-theme-primary text-theme-button border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                  >
                    <Truck className="h-3 w-3" />
                    選択した商品を発送
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4 mb-20">
            {loading ? (
              <div className="text-center py-12">
                <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">読み込み中...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white border-4 border-dashed border-gray-300 rounded-theme-lg p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 border-2 border-gray-300 rounded-theme-lg flex items-center justify-center mx-auto mb-4">
                  <Package className="h-8 w-8 text-gray-400" />
                </div>
                <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">商品がありません</p>
              </div>
            ) : activeTab === 'shipping' || activeTab === 'shipped' ? (
              productsByOrder.slice(0, displayCount).map(({ order_id, site, order_status, products }) => {
                const firstProduct = products[0];
                const trackingNumber = firstProduct.order_tracking_number;
                const statusLabel = statusLabels[order_status] || order_status;
                const canConfirmReceipt = order_status === 'delivered';

                const getStatusDisplay = () => {
                  switch (order_status) {
                    case 'preparing':
                      return { bgColor: 'bg-gray-50', badgeColor: 'bg-gray-100 text-gray-800', icon: <Clock className="h-3 w-3" />, message: '店舗が発送準備中です' };
                    case 'shipped':
                      return { bgColor: 'bg-yellow-50', badgeColor: 'bg-yellow-100 text-yellow-800', icon: <ShipIcon className="h-3 w-3" />, message: trackingNumber ? '商品が発送されました' : '発送済み' };
                    case 'in_transit':
                      return { bgColor: 'bg-blue-50', badgeColor: 'bg-blue-100 text-blue-800', icon: <Truck className="h-3 w-3" />, message: '配送業者が配送中です' };
                    case 'delivered':
                      return { bgColor: 'bg-green-50', badgeColor: 'bg-blue-100 text-blue-800', icon: <Package className="h-3 w-3" />, message: '配達完了しました。受取確認をお願いします。' };
                    case 'received':
                      return { bgColor: 'bg-green-50', badgeColor: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" />, message: '受取確認済みです。ご利用ありがとうございました。' };
                    default:
                      return { bgColor: 'bg-gray-50', badgeColor: 'bg-gray-100 text-gray-800', icon: null, message: '' };
                  }
                };

                const statusDisplay = getStatusDisplay();

                return (
                  <div key={order_id} className="bg-white border-theme rounded-theme-lg shadow-theme overflow-hidden">
                    <div className={`${statusDisplay.bgColor} px-4 py-3 border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-blue-400 border-theme rounded flex items-center justify-center">
                            <Store className="h-3 w-3 text-black" />
                          </div>
                          <h3 className="[font-weight:var(--theme-font-weight)] text-sm text-black">{site.name}</h3>
                          <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">({products.length}件)</span>
                        </div>
                        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-theme border-theme [font-weight:var(--theme-font-weight-button)] ${statusDisplay.badgeColor}`}>
                          {statusDisplay.icon}
                          <span>{statusLabel}</span>
                        </div>
                      </div>

                      {statusDisplay.message && (
                        <div className="text-xs font-medium text-gray-700 mb-2">{statusDisplay.message}</div>
                      )}

                      {trackingNumber && (
                        <div className="text-xs text-gray-700 mt-1 bg-white border-theme px-2 py-1.5 rounded-theme font-medium">
                          <span className="[font-weight:var(--theme-font-weight-button)]">追跡番号:</span> {trackingNumber}
                        </div>
                      )}

                      {canConfirmReceipt && (
                        <div className="mt-3">
                          <button
                            onClick={() => handleConfirmReceipt(order_id)}
                            disabled={confirmingReceipt === order_id}
                            className="w-full flex items-center justify-center gap-1 px-4 py-2 bg-green-400 border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
                          >
                            <CheckCircle className="h-4 w-4" />
                            {confirmingReceipt === order_id ? '確認中...' : '受取確認する'}
                          </button>
                        </div>
                      )}

                    </div>

                    <div className="divide-y divide-[var(--theme-border-color)]">
                      {products.map(product => (
                        <div key={product.lottery_result_id} className="p-4 bg-white">
                          <div className="flex gap-3">
                            <div className="w-16 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                              <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1 line-clamp-2">{product.name}</h4>
                              <span className="inline-block px-2 py-0.5 bg-theme-primary border-theme text-theme-button rounded-theme text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                              {product.event_name && (
                                <Link to={`/oripa/${product.event_uid}`} className="text-xs font-medium text-blue-600 hover:underline block mt-1">{product.event_name}</Link>
                              )}
                              <p className="text-xs font-medium text-gray-600 mt-1">
                                当選日時: {new Date(product.drawn_at).toLocaleString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            ) : activeTab === 'pending' || activeTab === 'converted' || activeTab === 'other' ? (
              filteredProducts.slice(0, displayCount).map(product => (
                activeTab === 'pending' ? (
                  <div key={product.lottery_result_id} className="bg-white border-theme rounded-theme-lg overflow-hidden shadow-theme">
                    <div className="p-4">
                      <div className="flex gap-3">
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          {product.auto_ship_deadline ? (
                            <div className="flex items-center gap-1 px-2 py-1.5 bg-orange-50 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-orange-700">
                              <CalendarClock className="h-3 w-3 flex-shrink-0" />
                              <span>{new Date(product.auto_ship_deadline).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}以降順次発送</span>
                            </div>
                          ) : canRequestShipping(product) ? (
                            <button onClick={() => handleShipSingle(product.lottery_result_id)} className="flex items-center justify-center gap-1 px-2 py-1.5 bg-theme-primary text-theme-button border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all">
                              <Truck className="h-3 w-3" />
                              発送
                            </button>
                          ) : isPast(product.shipping_request_deadline_at) ? (
                            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
                              <CalendarClock className="h-3 w-3 flex-shrink-0" />
                              <span>配送期限切れ</span>
                            </div>
                          ) : null}
                          {canRequestShipping(product) && shippablePendingProducts.length > 1 && (
                            <button
                              onClick={() => toggleShippingSelection(product.lottery_result_id)}
                              className={`flex items-center justify-center gap-1 px-2 py-1.5 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all ${
                                selectedShippingIds.has(product.lottery_result_id)
                                  ? 'bg-blue-400 text-black'
                                  : 'bg-white text-gray-700'
                              }`}
                            >
                              {selectedShippingIds.has(product.lottery_result_id) ? (
                                <CheckSquare className="h-3 w-3" />
                              ) : (
                                <Square className="h-3 w-3" />
                              )}
                              選択
                            </button>
                          )}
                          {canConvertToPoints(product) ? (
                            <button onClick={() => handleConvertSingle(product)} disabled={convertingId === product.lottery_result_id} className="flex items-center justify-center gap-1 px-2 py-1.5 bg-green-400 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50">
                              <Coins className="h-3 w-3" />
                              {convertingId === product.lottery_result_id ? '...' : 'PT交換'}
                            </button>
                          ) : isPast(product.point_exchange_deadline_at) ? (
                            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
                              <CalendarClock className="h-3 w-3 flex-shrink-0" />
                              <span>PT期限切れ</span>
                            </div>
                          ) : null}
                        </div>
                        <div className="w-20 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                          <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black line-clamp-2 mb-1">{product.name}</h3>
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <span className="px-1.5 py-0.5 bg-theme-primary border-theme text-theme-button rounded text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                            <span className="px-1.5 py-0.5 border-theme text-black rounded text-xs [font-weight:var(--theme-font-weight-button)] bg-blue-400">オリパ</span>
                          </div>
                          {product.event_name && (
                            <Link to={`/oripa/${product.event_uid}`} className="text-xs font-medium text-blue-600 hover:underline block mb-0.5">{product.event_name}</Link>
                          )}
                          <p className="text-xs font-medium text-gray-600">
                            当選日時: {new Date(product.drawn_at).toLocaleString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {product.shipping_fee != null && product.shipping_fee > 0 && (
                            <p className="text-xs font-medium text-orange-700 mt-0.5">
                              配送料: {product.shipping_fee.toLocaleString()}pt
                            </p>
                          )}
                          {canConvertToPoints(product) && (
                            <div className="flex items-center gap-1 mt-1">
                              <Coins className="h-3 w-3 text-yellow-600" />
                              <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-yellow-600">
                                {product.point_value.toLocaleString()} PT
                                {product.point_exchange_deadline_at && <span className="text-gray-500">（交換期限: {new Date(product.point_exchange_deadline_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}まで）</span>}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : activeTab === 'converted' ? (
                  <div key={product.lottery_result_id} className="bg-white border-theme rounded-theme-lg overflow-hidden shadow-theme">
                    <div className="p-4">
                      <div className="flex gap-4">
                        <div className="w-24 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                          <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base [font-weight:var(--theme-font-weight-button)] text-black line-clamp-2 mb-2">{product.name}</h3>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="px-2 py-1 bg-theme-primary border-theme text-theme-button rounded-theme text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                            <span className="px-2 py-1 border-theme text-black rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] bg-blue-400">オリパ</span>
                          </div>
                          {product.event_name && (
                            <Link to={`/oripa/${product.event_uid}`} className="text-xs font-medium text-blue-600 hover:underline block mb-2">{product.event_name}</Link>
                          )}
                          <div className="flex items-center gap-2 mt-2 bg-green-50 border-2 border-green-200 rounded-theme px-3 py-2">
                            <Coins className="h-4 w-4 text-green-600" />
                            <div>
                              <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-green-700">{product.converted_point_value?.toLocaleString()} PT に交換済み</p>
                              <p className="text-xs text-green-600">
                                {product.point_converted_at && new Date(product.point_converted_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div key={product.lottery_result_id} className="bg-white border-theme rounded-theme-lg overflow-hidden shadow-theme opacity-90">
                    <div className="p-4">
                      <div className="flex gap-4">
                        <div className="w-24 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                          <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain grayscale" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base [font-weight:var(--theme-font-weight-button)] text-black line-clamp-2 mb-2">{product.name}</h3>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="px-2 py-1 bg-theme-primary border-theme text-theme-button rounded-theme text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                            <span className="px-2 py-1 border-theme text-gray-700 rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] bg-gray-100">対象外</span>
                          </div>
                          {product.event_name && (
                            <Link to={`/oripa/${product.event_uid}`} className="text-xs font-medium text-blue-600 hover:underline block mb-2">{product.event_name}</Link>
                          )}
                          <div className="bg-gray-50 border-2 border-gray-200 rounded-theme px-3 py-2">
                            <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700">この商品は現在、PT交換および配送申請の対象外です。</p>
                          </div>
                          <p className="text-xs font-medium text-gray-600 mt-2">
                            当選日時: {new Date(product.drawn_at).toLocaleString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ))
            ) : null}

            {!loading && (() => {
              const total = (activeTab === 'shipping' || activeTab === 'shipped') ? productsByOrder.length : filteredProducts.length;
              return total > displayCount || hasMore ? (
                <button onClick={handleLoadMore} disabled={loadingMore} className="w-full py-3 bg-white border-theme rounded-theme text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50">
                  {loadingMore ? '読み込み中...' : 'もっと見る'}
                </button>
              ) : null;
            })()}
          </div>

              </section>
            </div>
          </div>
        </div>
      </div>

      {showBulkDialog && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => !bulkConverting && setShowBulkDialog(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <h2 className="text-base [font-weight:var(--theme-font-weight)] text-black">一括PT交換</h2>
                <p className="text-xs text-gray-500">交換しない商品はチェックを外してください</p>
              </div>
              <button onClick={() => !bulkConverting && setShowBulkDialog(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {convertibleGroups.map(group => {
                const selectionState = getGroupSelectionState(group);
                const isExpanded = expandedGroups.has(group.name);
                const selectedCount = group.items.filter(p => !bulkExcludedIds.has(p.lottery_result_id)).length;
                const groupTotal = group.items.filter(p => !bulkExcludedIds.has(p.lottery_result_id)).reduce((sum, p) => sum + p.point_value, 0);
                return (
                  <div key={group.name}>
                    <div className={`flex items-center gap-3 p-3 rounded-theme border-theme transition-all ${selectionState !== 'none' ? 'bg-green-50' : 'bg-gray-50 opacity-60'}`}>
                      <button onClick={(e) => { e.stopPropagation(); toggleGroupSelection(group); }} className="flex-shrink-0">
                        {selectionState === 'all' ? (
                          <CheckSquare className="h-5 w-5 text-green-600" />
                        ) : selectionState === 'partial' ? (
                          <div className="h-5 w-5 border-2 border-green-600 rounded flex items-center justify-center bg-green-600">
                            <Minus className="h-3 w-3 text-white" />
                          </div>
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <button onClick={() => toggleGroupExpand(group.name)} className="flex-1 flex items-center gap-3 min-w-0">
                        <div className="w-10 h-14 bg-gray-100 rounded overflow-hidden flex-shrink-0 border-theme">
                          <ResponsiveImage src={group.image} variants={group.variants} alt={group.name} displaySize="card" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-black line-clamp-1">{group.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs px-1.5 py-0.5 bg-theme-primary border-theme text-theme-button rounded [font-weight:var(--theme-font-weight-button)]">{group.rarity}</span>
                            <span className="text-xs text-gray-500">{selectedCount}/{group.items.length}件</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-green-700">{groupTotal.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">PT</p>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="ml-8 mt-1 space-y-1">
                        {group.items.map(item => {
                          const isItemSelected = !bulkExcludedIds.has(item.lottery_result_id);
                          return (
                            <button
                              key={item.lottery_result_id}
                              onClick={() => toggleItemSelection(item.lottery_result_id)}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-theme text-left transition-all ${isItemSelected ? 'bg-green-50/50' : 'bg-gray-50 opacity-60'}`}
                            >
                              {isItemSelected ? <CheckSquare className="h-4 w-4 text-green-600 flex-shrink-0" /> : <Square className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <span className="text-xs text-gray-500 flex-shrink-0">#{item.lottery_result_id}</span>
                              <span className="flex-1 text-sm [font-weight:var(--theme-font-weight-button)] text-green-700">{item.point_value.toLocaleString()} PT</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">{selectedForBulk.length}件選択中（上限{BULK_CONVERT_LIMIT}件）</span>
                <span className="text-base [font-weight:var(--theme-font-weight)] text-green-700">合計 {bulkTotalPoints.toLocaleString()} PT</span>
              </div>
              {isBulkOverLimit && (
                <p className="text-sm text-red-500 mb-2">一度に交換できるのは{BULK_CONVERT_LIMIT}件までです。選択を減らしてください。</p>
              )}
              <button
                onClick={handleBulkConvert}
                disabled={bulkConverting || selectedForBulk.length === 0 || isBulkOverLimit}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-400 border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50"
              >
                <Coins className="h-4 w-4" />
                {bulkConverting ? '交換中...' : 'PT交換する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OripaProductsPage;
